/**
 * The clauses every surface builds a node's status from, decided once.
 *
 * `core/vocabulary.ts` holds the words; this holds the two rules that choose
 * between them, because four surfaces print a node's state — the card's status
 * line, the thread's folded strip, the ancestor cards on the path, and the node
 * tree's accessible names — and each once carried its own copy of the rules. They
 * had already split: the tree said a reading was "checked before a rewrite" where
 * the card printed the bare reading about the same stale check, and the strip said
 * "result recorded" for a result that had already become the claim's ending,
 * where the card and the ancestor cards stayed silent about it. The words were one
 * table's by then (D166); the conditions were not.
 *
 * A leaf on the consumer side: the UI and the canvas import this, nothing in
 * `core/` does, so the runtime reads of `types.ts` and `endings.ts` cannot close
 * a cycle.
 */

import type { ReasonNode } from './types'
import { READING_LABEL, retractedWorks, verificationLive } from './types'
import { endingOf } from './endings'
import type { EvidenceChange } from './rebase'
import type { RefutedUnder } from './taint'
import { STATUS_WORD, citesRetractedCount } from './vocabulary'
import { latestPriorArt, laterStatements, priorArtLabel, priorArtLive } from './priorArt'

/**
 * A check's reading as every surface prints it, or null with no check. A check
 * that read the claim before somebody rewrote it still stands on the record, but
 * it read other words — so it says so, in the same breath as its reading.
 */
export function readingWord(n: ReasonNode): string | null {
  const v = n.verification
  if (!v) return null
  return verificationLive(n)
    ? READING_LABEL[v.reading]
    : `${READING_LABEL[v.reading]}, ${STATUS_WORD.checkedBeforeRewrite.toLowerCase()}`
}

/**
 * A conjecture's prior-art reading, or the words for none — null for any other
 * node. A conjecture's search is its check: no source check runs on one (D281),
 * so "not checked yet" said nothing true about it, and the spatial view already
 * lifts one a search has read to the checked tier (D279) while the tree said
 * less than the picture (D289). The label is the report card's and the record's
 * (`priorArtLabel`, later statements included), and a search of words since
 * rewritten says so as a stale check does, in the same breath.
 */
export function priorArtWord(n: ReasonNode): string | null {
  const c = n.conjecture
  if (n.kind !== 'conjecture' || !c) return null
  const s = latestPriorArt(n)
  if (!s) return STATUS_WORD.notSearchedForPriorArt.toLowerCase()
  const label = priorArtLabel(s, laterStatements(c, s))
  return priorArtLive(n, s) ? label : `${label}, ${STATUS_WORD.checkedBeforeRewrite.toLowerCase()}`
}

/**
 * A recorded result, as a word, when it is not already the node's ending. A result
 * that settled the claim is printed as the ending ("answered by your result"); one
 * that settled nothing is still worth a word.
 */
export function outcomeWord(n: ReasonNode): string | null {
  if (!n.outcome || endingOf(n) === 'answered') return null
  return STATUS_WORD.resultRecorded.toLowerCase()
}

/**
 * What the claim stands on that has since been refuted, as the clause a surface
 * without a link prints — "stands on something refuted: <claim>" for the tree's
 * accessible name, and the bare words where a line has one line's budget (the
 * strip, the path's ancestor cards), whose press opens the card that names it.
 * The card prints the claim as a press. `refutedUnder` (`core/taint.ts`) decides.
 *
 * Those four surfaces said "needs a second look" for it, which is the word for
 * ANY changed source record above a claim: a supported check turning mixed reads
 * the same. And a refuting RESULT moves no source record at all, so under one the
 * card said nothing but "not checked yet" while the canvas detached the strand.
 * The report had the words all along ("Claims standing on something refuted");
 * this puts them on the node. The claim it names is the refuted one, never the
 * parent: the parent was not refuted.
 */
export const standsOnWord = (r: RefutedUnder, named = true): string =>
  `${STATUS_WORD.standsOnRefuted.toLowerCase()}${named ? `: ${r.source.label}` : ''}`

/**
 * The changed source record a status line should still name, given the
 * refutation it already names — or none.
 *
 * A check that refuted a claim is ALSO a changed record for everything reasoned
 * below it, so the two clauses fired together about one event: "stands on
 * something refuted · needs a second look (the check on … changed)", both
 * pointing at the same claim. The refutation is the more specific sentence and
 * says it alone. Any OTHER changed record still has its clause — a synthesis can
 * stand on a refutation through one branch and on a changed check through the
 * other — and so does the same record once a rethink has answered it, because
 * "prior evidence version" says something the refutation does not.
 */
export function secondLookChange(
  changes: readonly EvidenceChange[] | undefined,
  refuted: RefutedUnder | null,
): EvidenceChange | undefined {
  if (!changes?.length) return undefined
  if (!refuted) return changes[0]
  return changes.find((c) => c.sourceId !== refuted.source.id || c.rebasedAt !== undefined)
}

/**
 * The retraction clause, or null: "cites a retracted work", or "cites 2
 * retracted works". Only on a live check (`retractedWorks`), because a check of
 * an earlier wording is not what the claim now rests on — the same rule the
 * coach's cue fires on, so the card and the coach cannot disagree about one node.
 */
export function retractedWord(n: ReasonNode): string | null {
  const works = retractedWorks(n).length
  return works ? citesRetractedCount(works) : null
}
